// engagement-components/LandingPageManager.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Row, Col, Card, Form, Button, Badge, Modal, Alert, Spinner,
  OverlayTrigger, Tooltip, ProgressBar,
} from "react-bootstrap";
import {
  FaPlus, FaEdit, FaTrash, FaArrowLeft, FaSave, FaEye,
  FaMobile, FaDesktop, FaExternalLinkAlt, FaCopy, FaGripVertical,
  FaUpload, FaImage, FaTimes, FaLink,
} from "react-icons/fa";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import axios from "axios";
import { apiUrl, authHeaders, cdnUrl, SAMPLE, uploadHeaders } from "./config";

// ─── Font options (matching SurveyEditor branding pattern) ────────────────────
const FONT_OPTIONS = [
  { label: "Default (System)",  value: "inherit" },
  { label: "Playfair Display",  value: "'Playfair Display', serif",    gf: "Playfair+Display:wght@400;700" },
  { label: "Poppins",           value: "'Poppins', sans-serif",         gf: "Poppins:wght@400;600;700" },
  { label: "Lato",              value: "'Lato', sans-serif",            gf: "Lato:wght@400;700" },
  { label: "Merriweather",      value: "'Merriweather', serif",         gf: "Merriweather:wght@400;700" },
  { label: "Montserrat",        value: "'Montserrat', sans-serif",      gf: "Montserrat:wght@400;700" },
  { label: "Dancing Script",    value: "'Dancing Script', cursive",     gf: "Dancing+Script:wght@600;700" },
  { label: "Cinzel",            value: "'Cinzel', serif",               gf: "Cinzel:wght@400;700" },
  { label: "Great Vibes",       value: "'Great Vibes', cursive",        gf: "Great+Vibes" },
];

// ─── Section type catalogue ────────────────────────────────────────────────────
const SECTION_TYPES = [
  { type: "banner",        label: "Banner Image",   icon: "fa-image",       color: "#0d6efd", desc: "Full-width hero photo with optional text overlay" },
  { type: "slideshow",     label: "Slideshow",      icon: "fa-film",        color: "#6610f2", desc: "Auto-rotating image carousel" },
  { type: "gallery",       label: "Photo Grid",     icon: "fa-images",      color: "#6f42c1", desc: "2, 4 or 6 photo grid layout" },
  { type: "title",         label: "Headline",       icon: "fa-heading",     color: "#0dcaf0", desc: "Bold heading with font & colour control" },
  { type: "description",   label: "Description",    icon: "fa-align-left",  color: "#198754", desc: "Offer details paragraph" },
  { type: "price",         label: "Price Block",    icon: "fa-rupee-sign",  color: "#ffc107", desc: "Pricing / discount info" },
  { type: "cta",           label: "CTA Buttons",    icon: "fa-hand-pointer",color: "#dc3545", desc: "Book Now, WhatsApp, Call" },
  { type: "feedback_form", label: "Feedback Form",  icon: "fa-poll",        color: "#20c997", desc: "Embed a feedback survey link — guest taps to fill the form" },
  { type: "divider",       label: "Divider",        icon: "fa-minus",       color: "#6c757d", desc: "Horizontal separator" },
  { type: "custom_text",   label: "Custom Text",    icon: "fa-paragraph",   color: "#6f42c1", desc: "T&C, notes, extras" },
];

const makeId = () => `s_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`;

const newSection = (type) => {
  const base  = { id: makeId(), type };
  const style = { font: "inherit", size: 16, color: "#212529", bold: false, italic: false, align: "left" };
  const bg    = { type: "none", color: "#ffffff", gradStart: "#0d6efd", gradEnd: "#0dcaf0", gradDir: "135deg" };

  if (type === "banner") return {
    ...base, value: "",
    heightPct: 40,          // % of viewport height: 20–100
    bg: { ...bg, type: "image" },
    overlay: { text: "", font: "inherit", size: 28, color: "#ffffff", bold: true, italic: false, opacity: 0.45, bgColor: "#000000", align: "center" },
  };

  if (type === "cta") return {
    ...base,
    bg: { ...bg, type: "none" },
    padding: 16,
    buttons: [
      { id: "b1", label: "Book Now",    action: "booking",  url: "", style: "fill",    color: "#0d6efd", textColor: "#ffffff", rounded: true },
      { id: "b2", label: "WhatsApp Us", action: "whatsapp", url: "", style: "fill",    color: "#198754", textColor: "#ffffff", rounded: true },
      { id: "b3", label: "Call Us",     action: "call",     url: "", style: "outline", color: "#6c757d", textColor: "#6c757d", rounded: true },
    ],
  };

  if (type === "gallery")  return { ...base, images: ["","",""], cols: 2 };
  if (type === "slideshow") return {
    ...base, images: ["","",""], interval: 3000, showDots: true,
    overlays: Array(3).fill(null).map(() => ({
      text: "", font: "inherit", size: 28, color: "#ffffff", bold: false, italic: false, opacity: 0.5, bgColor: "#000000", align: "center",
    })),
  };
  if (type === "feedback_form") return {
    ...base, surveyId: "", surveyTitle: "", buttonLabel: "Share Your Feedback", buttonColor: "#198754", note: "",
  };

  // title, description, price, custom_text — all get style + bg
  return { ...base, value: "", style: { ...style }, bg: { ...bg } };
};

const DEFAULT_SECTIONS = () => [
  newSection("banner"),
  newSection("title"),
  newSection("description"),
  newSection("price"),
  newSection("cta"),
];

const SAMPLE_PAGES = [
  { id: "lp1", title: "Weekend Spa Package",   slug: "weekend-spa",     status: "Active", views: 320,  clicks: 89  },
  { id: "lp2", title: "New Year Dinner Offer", slug: "new-year-dinner", status: "Active", views: 1240, clicks: 412 },
  { id: "lp3", title: "Birthday Special Room", slug: "birthday-room",   status: "Draft",  views: 0,    clicks: 0   },
];

// ─── Image Upload Hook ─────────────────────────────────────────────────────────
const useImageUpload = () => {
  const [uploading, setUploading] = useState(false);
  const [progress,  setProgress]  = useState(0);

  const upload = async (file) => {
    if (!file) return null;
    setUploading(true);
    setProgress(0);
    try {
      const formData = new FormData();
      formData.append("image", file); // field name must be "image" for multer

      const res = await axios.post(
        `${apiUrl}/feedback/landing-pages/upload-image`,
        formData,
        {
          ...uploadHeaders(),           // Auth only — NO Content-Type override
          onUploadProgress: (e) => {
            setProgress(Math.round((e.loaded / e.total) * 100));
          },
        }
      );

      return res.data?.url || null;
    } catch {
      // Fallback: local object URL so UI still works in demo mode
      return URL.createObjectURL(file);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return { upload, uploading, progress };
};

// ─── ImageUploadField — reusable image picker with upload + URL fallback ───────
const ImageUploadField = ({ value, onChange, label = "Image", aspectHint = "16:9 recommended" }) => {
  const fileRef = useRef();
  const { upload, uploading, progress } = useImageUpload();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await upload(file);
    if (url) onChange(url);
    e.target.value = "";
  };

  return (
    <div>
      {/* Preview */}
      {value ? (
        <div className="position-relative mb-2">
          <img
            src={value} alt="preview"
            className="w-100 rounded"
            style={{ height: 100, objectFit: "cover", border: "1px solid #dee2e6" }}
            onError={(e) => { e.target.src = ""; e.target.style.display = "none"; }}
          />
          <Button
            variant="danger" size="sm"
            className="position-absolute top-0 end-0 m-1 p-1"
            style={{ lineHeight: 1 }}
            onClick={() => onChange("")}
            title="Remove image"
          >
            <FaTimes size={10} />
          </Button>
        </div>
      ) : (
        <div
          className="rounded d-flex flex-column align-items-center justify-content-center mb-2"
          style={{ height: 80, border: "2px dashed #dee2e6", background: "#f8f9fa", cursor: "pointer" }}
          onClick={() => fileRef.current?.click()}
        >
          <FaImage color="#adb5bd" size={20} />
          <small className="text-muted mt-1">{label}</small>
          <small className="text-muted" style={{ fontSize: "0.68rem" }}>{aspectHint}</small>
        </div>
      )}

      {/* Upload progress */}
      {uploading && (
        <ProgressBar now={progress} label={`${progress}%`} size="sm" className="mb-2" style={{ height: 6 }} />
      )}

      {/* Controls */}
      <div className="d-flex gap-2">
        <Button
          variant="outline-primary" size="sm"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex-grow-1"
        >
          <FaUpload className="me-1" />
          {uploading ? "Uploading..." : "Upload Image"}
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          style={{ display: "none" }}
          onChange={handleFile}
          aria-label={`Upload ${label}`}
        />
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>Paste image URL instead</Tooltip>}
        >
          <Button
            variant="outline-secondary" size="sm"
            onClick={() => {
              const url = window.prompt("Paste image URL:");
              if (url?.trim()) onChange(url.trim());
            }}
          >
            <FaLink />
          </Button>
        </OverlayTrigger>
      </div>
    </div>
  );
};

// ─── StylePanel — reusable text style controls (font/size/color/align/bold/italic)
// matching SurveyEditor branding pattern exactly
const StylePanel = ({ style = {}, onChange, showFont = true, label = "Text Style", defaultSize = 16 }) => {
  const s      = style;
  const update = (key, val) => onChange({ ...s, [key]: val });
  const loadFont = (opt) => {
    if (!opt?.gf) return;
    const id = `gf-${opt.gf}`;
    if (document.getElementById(id)) return;
    const l = document.createElement("link");
    l.id = id; l.rel = "stylesheet";
    l.href = `https://fonts.googleapis.com/css2?family=${opt.gf}&display=swap`;
    document.head.appendChild(l);
  };

  return (
    <div className="p-2 rounded border mt-2" style={{ background: "#f8faff", borderColor: "#c7d2fe" }}>
      <small className="fw-semibold text-primary d-block mb-2">
        <i className="fas fa-paint-brush me-1" />{label}
      </small>
      <Row className="g-2">
        {showFont && (
          <Col xs={12}>
            <Form.Label style={{ fontSize: "0.7rem", fontWeight: 600 }}>Font family</Form.Label>
            <Form.Select size="sm" value={s.font || "inherit"}
              onChange={(e) => { loadFont(FONT_OPTIONS.find((x) => x.value === e.target.value)); update("font", e.target.value); }}>
              {FONT_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </Form.Select>
          </Col>
        )}
        <Col xs={6}>
          <Form.Label style={{ fontSize: "0.7rem", fontWeight: 600 }}>
            Size — {s.size || defaultSize}px
          </Form.Label>
          <Form.Control type="range" min={10} max={72} step={1}
            value={s.size || defaultSize}
            onChange={(e) => update("size", Number(e.target.value))} />
        </Col>
        <Col xs={3}>
          <Form.Label style={{ fontSize: "0.7rem", fontWeight: 600 }}>Colour</Form.Label>
          <Form.Control type="color" value={s.color || "#212529"}
            onChange={(e) => update("color", e.target.value)}
            style={{ padding: "2px 4px", height: 32, width: "100%" }} />
        </Col>
        <Col xs={3}>
          <Form.Label style={{ fontSize: "0.7rem", fontWeight: 600 }}>Align</Form.Label>
          <Form.Select size="sm" value={s.align || "left"} onChange={(e) => update("align", e.target.value)}>
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </Form.Select>
        </Col>
        <Col xs={12} className="d-flex gap-3">
          <Form.Check type="checkbox" id={`bold-${Math.random()}`} label="Bold"
            checked={!!s.bold} onChange={(e) => update("bold", e.target.checked)} />
          <Form.Check type="checkbox" id={`italic-${Math.random()}`} label="Italic"
            checked={!!s.italic} onChange={(e) => update("italic", e.target.checked)} />
        </Col>
      </Row>
    </div>
  );
};

// ─── BgPanel — background: none / solid color / linear gradient
const BgPanel = ({ bg = {}, onChange, label = "Background" }) => {
  const b      = bg;
  const update = (key, val) => onChange({ ...b, [key]: val });
  const DIRS   = [
    { value: "to right",   label: "→ Left to right"   },
    { value: "to left",    label: "← Right to left"   },
    { value: "to bottom",  label: "↓ Top to bottom"   },
    { value: "135deg",     label: "↘ Diagonal"        },
    { value: "to top right", label: "↗ Diagonal up"  },
  ];

  const bgPreview = b.type === "gradient"
    ? `linear-gradient(${b.gradDir || "135deg"}, ${b.gradStart || "#0d6efd"}, ${b.gradEnd || "#0dcaf0"})`
    : b.type === "solid" ? b.color || "#ffffff"
    : "transparent";

  return (
    <div className="p-2 rounded border mt-2" style={{ background: "#fff8f0", borderColor: "#fed7aa" }}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <small className="fw-semibold" style={{ color: "#c2410c" }}>
          <i className="fas fa-fill-drip me-1" />{label}
        </small>
        {/* Live preview swatch */}
        {b.type !== "none" && (
          <div style={{ width: 40, height: 20, borderRadius: 4, background: bgPreview, border: "1px solid #e5e7eb", flexShrink: 0 }} />
        )}
      </div>
      <Row className="g-2">
        <Col xs={12}>
          <Form.Label style={{ fontSize: "0.7rem", fontWeight: 600 }}>Background type</Form.Label>
          <div className="d-flex gap-2">
            {[
              { v: "none",     label: "None",     icon: "fa-ban"        },
              { v: "solid",    label: "Solid",    icon: "fa-square"     },
              { v: "gradient", label: "Gradient", icon: "fa-palette"    },
            ].map(({ v, label: lbl, icon }) => (
              <button key={v}
                onClick={() => update("type", v)}
                className="border d-flex align-items-center gap-1"
                style={{
                  padding: "4px 10px", borderRadius: 6, fontSize: "0.76rem", cursor: "pointer",
                  background: b.type === v ? "#fff7ed" : "#fff",
                  borderColor: b.type === v ? "#c2410c" : "#dee2e6",
                  color:       b.type === v ? "#c2410c" : "#6b7280",
                  fontWeight:  b.type === v ? 600 : 400,
                }}>
                <i className={`fas ${icon}`} style={{ fontSize: "0.68rem" }} /> {lbl}
              </button>
            ))}
          </div>
        </Col>

        {b.type === "solid" && (
          <Col xs={12} className="d-flex align-items-center gap-3">
            <Form.Label className="mb-0 fw-semibold" style={{ fontSize: "0.7rem" }}>Colour</Form.Label>
            <Form.Control type="color" value={b.color || "#ffffff"}
              onChange={(e) => update("color", e.target.value)}
              style={{ padding: "2px 4px", height: 32, width: 60 }} />
            <span style={{ fontSize: "0.76rem", color: "#6b7280" }}>{b.color || "#ffffff"}</span>
          </Col>
        )}

        {b.type === "gradient" && (
          <>
            <Col xs={5}>
              <Form.Label style={{ fontSize: "0.7rem", fontWeight: 600 }}>Start colour</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <Form.Control type="color" value={b.gradStart || "#0d6efd"}
                  onChange={(e) => update("gradStart", e.target.value)}
                  style={{ padding: "2px 4px", height: 32, width: 50 }} />
                <span style={{ fontSize: "0.72rem", color: "#6b7280" }}>{b.gradStart || "#0d6efd"}</span>
              </div>
            </Col>
            <Col xs={5}>
              <Form.Label style={{ fontSize: "0.7rem", fontWeight: 600 }}>End colour</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <Form.Control type="color" value={b.gradEnd || "#0dcaf0"}
                  onChange={(e) => update("gradEnd", e.target.value)}
                  style={{ padding: "2px 4px", height: 32, width: 50 }} />
                <span style={{ fontSize: "0.72rem", color: "#6b7280" }}>{b.gradEnd || "#0dcaf0"}</span>
              </div>
            </Col>
            <Col xs={12}>
              <Form.Label style={{ fontSize: "0.7rem", fontWeight: 600 }}>Direction</Form.Label>
              <Form.Select size="sm" value={b.gradDir || "135deg"} onChange={(e) => update("gradDir", e.target.value)}>
                {DIRS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </Form.Select>
            </Col>
            {/* Live gradient preview bar */}
            <Col xs={12}>
              <div style={{
                height: 24, borderRadius: 6,
                background: `linear-gradient(${b.gradDir || "135deg"}, ${b.gradStart || "#0d6efd"}, ${b.gradEnd || "#0dcaf0"})`,
                border: "1px solid #e5e7eb",
              }} />
            </Col>
          </>
        )}
      </Row>
    </div>
  );
};

// ─── Section Editor Row ────────────────────────────────────────────────────────
const SectionRow = ({ section, dragHandleProps, onUpdate, onRemove, onUpdateButton, onUpdateGallery }) => {
  const def  = SECTION_TYPES.find((t) => t.type === section.type) || {};
  const upd  = (field, val) => onUpdate(section.id, field, val);
  const updS = (key, val)   => upd("style", { ...(section.style || {}), [key]: val });
  const updB = (val)        => upd("bg", val);

  return (
    <div className="border rounded mb-2 overflow-hidden" style={{ borderLeft: `4px solid ${def.color}` }}>
      <div className="d-flex align-items-center gap-2 px-3 py-2 bg-light border-bottom">
        <div {...dragHandleProps} style={{ cursor: "grab" }} className="text-muted"><FaGripVertical size={13} /></div>
        <i className={`fas ${def.icon} me-1`} style={{ color: def.color, fontSize: "0.78rem" }}></i>
        <small className="fw-semibold" style={{ color: def.color, fontSize: "0.78rem" }}>{def.label}</small>
        <Button variant="link" size="sm" className="p-0 text-danger ms-auto"
          onClick={() => onRemove(section.id)}><FaTrash size={10} /></Button>
      </div>

      <div className="p-3">

        {/* ── BANNER ─────────────────────────────────────────────────────── */}
        {section.type === "banner" && (
          <div>
            <ImageUploadField value={section.value || ""} onChange={(url) => upd("value", url)}
              label="Banner image" aspectHint="Recommended: 1200×400px or wider" />

            {/* Height control */}
            <div className="mt-2">
              <Form.Label style={{ fontSize: "0.72rem", fontWeight: 600 }}>
                Height — <strong>{section.heightPct || 40}%</strong> of screen
              </Form.Label>
              <Form.Control type="range" min={15} max={100} step={5}
                value={section.heightPct || 40}
                onChange={(e) => upd("heightPct", Number(e.target.value))} />
              <div className="d-flex justify-content-between text-muted" style={{ fontSize: "0.68rem" }}>
                <span>15% (compact strip)</span><span>50% (half screen)</span><span>100% (full screen)</span>
              </div>
            </div>

            {/* Background — shown when no image, or as tint over image */}
            <BgPanel bg={section.bg || {}} onChange={updB}
              label={section.value ? "Background tint / gradient (over image)" : "Background (no image)"} />

            {/* Text overlay */}
            <div className="p-2 rounded border mt-2" style={{ background: "#f0f4ff", borderColor: "#c7d2fe" }}>
              <small className="fw-semibold text-primary d-block mb-2">
                <i className="fas fa-font me-1" />Text overlay on banner
              </small>
              <Form.Control size="sm" className="mb-2"
                value={section.overlay?.text || ""}
                onChange={(e) => upd("overlay", { ...(section.overlay||{}), text: e.target.value })}
                placeholder="e.g. 🎂 Happy Birthday! · Leave blank for no text" />
              {section.overlay?.text && (
                <StylePanel
                  style={section.overlay}
                  onChange={(v) => upd("overlay", { ...(section.overlay||{}), ...v })}
                  label="Overlay text style"
                  defaultSize={28}
                />
              )}
              {/* Overlay background opacity */}
              <div className="mt-2">
                <Form.Label style={{ fontSize: "0.7rem", fontWeight: 600 }}>
                  Dark overlay opacity — {Math.round((section.overlay?.opacity || 0) * 100)}%
                </Form.Label>
                <Form.Control type="range" min={0} max={1} step={0.05}
                  value={section.overlay?.opacity || 0}
                  onChange={(e) => upd("overlay", { ...(section.overlay||{}), opacity: Number(e.target.value) })} />
                <div className="d-flex align-items-center gap-2 mt-1">
                  <Form.Label className="mb-0" style={{ fontSize: "0.7rem" }}>Overlay bg colour</Form.Label>
                  <Form.Control type="color" value={section.overlay?.bgColor || "#000000"}
                    onChange={(e) => upd("overlay", { ...(section.overlay||{}), bgColor: e.target.value })}
                    style={{ padding: "2px 4px", height: 28, width: 50 }} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── GALLERY ────────────────────────────────────────────────────── */}
        {section.type === "gallery" && (
          <div>
            <small className="text-muted d-block mb-2">Upload up to 3 images shown side by side</small>
            <Row className="g-2">
              {[0,1,2].map((i) => (
                <Col xs={4} key={i}>
                  <ImageUploadField value={section.images?.[i] || ""}
                    onChange={(url) => onUpdateGallery(section.id, i, url)}
                    label={`Photo ${i+1}`} aspectHint="1:1" />
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* ── TITLE ──────────────────────────────────────────────────────── */}
        {section.type === "title" && (
          <div>
            <Form.Control size="sm" className="mb-2"
              value={section.value || ""}
              onChange={(e) => upd("value", e.target.value)}
              placeholder="e.g. Weekend Spa Delight ✨"
              style={{
                fontFamily: section.style?.font || "inherit",
                fontSize: `${section.style?.size || 24}px`,
                fontWeight: section.style?.bold ? 700 : 400,
                fontStyle: section.style?.italic ? "italic" : "normal",
                color: section.style?.color || "#212529",
                textAlign: section.style?.align || "left",
              }} />
            <StylePanel style={section.style || {}} onChange={(v) => upd("style", v)} defaultSize={24} />
            <BgPanel bg={section.bg || {}} onChange={updB} />
          </div>
        )}

        {/* ── DESCRIPTION / CUSTOM TEXT ───────────────────────────────────  */}
        {["description", "custom_text"].includes(section.type) && (
          <div>
            <Form.Control as="textarea" rows={3} size="sm" className="mb-1"
              value={section.value || ""}
              onChange={(e) => upd("value", e.target.value)}
              placeholder={section.type === "description"
                ? "Describe your offer — what's included, what makes it special..."
                : "T&C, cancellation policy, notes..."}
              style={{
                resize: "none",
                fontFamily: section.style?.font || "inherit",
                fontSize: `${section.style?.size || 14}px`,
                color: section.style?.color || "#374151",
                textAlign: section.style?.align || "left",
              }} />
            <StylePanel style={section.style || {}} onChange={(v) => upd("style", v)} defaultSize={14} />
            <BgPanel bg={section.bg || {}} onChange={updB} />
          </div>
        )}

        {/* ── PRICE ──────────────────────────────────────────────────────── */}
        {section.type === "price" && (
          <div>
            <Form.Control size="sm" className="mb-1"
              value={section.value || ""}
              onChange={(e) => upd("value", e.target.value)}
              placeholder="e.g. ₹2,999 per couple | Save 30%"
              style={{
                fontFamily: section.style?.font || "inherit",
                fontSize: `${section.style?.size || 18}px`,
                fontWeight: section.style?.bold !== false ? 700 : 400,
                color: section.style?.color || "#dc2626",
                textAlign: section.style?.align || "left",
              }} />
            <StylePanel style={section.style || {}} onChange={(v) => upd("style", v)} defaultSize={18} />
            <BgPanel bg={section.bg || {}} onChange={updB} />
          </div>
        )}

        {/* ── DIVIDER ────────────────────────────────────────────────────── */}
        {section.type === "divider" && (
          <div className="text-center text-muted py-1" style={{ fontSize: "0.72rem" }}>── horizontal rule ──</div>
        )}

        {/* ── CTA BUTTONS ────────────────────────────────────────────────── */}
        {section.type === "cta" && (
          <div>
            {/* Padding control */}
            <div className="mb-3">
              <Form.Label style={{ fontSize: "0.72rem", fontWeight: 600 }}>
                Section padding — {section.padding || 16}px top/bottom
              </Form.Label>
              <Form.Control type="range" min={0} max={48} step={4}
                value={section.padding || 16}
                onChange={(e) => upd("padding", Number(e.target.value))} />
            </div>

            {/* Button rows */}
            {section.buttons?.map((btn, bIdx) => (
              <div key={btn.id} className="p-2 rounded border mb-2" style={{ background: "#f9fafb" }}>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <small className="fw-semibold text-muted">Button {bIdx + 1}</small>
                  {section.buttons.length > 1 && (
                    <Button variant="link" size="sm" className="p-0 text-danger"
                      onClick={() => upd("buttons", section.buttons.filter((_, i) => i !== bIdx))}>
                      <FaTrash size={10} />
                    </Button>
                  )}
                </div>
                <Row className="g-1 mb-2">
                  <Col xs={6}>
                    <Form.Control size="sm" value={btn.label}
                      onChange={(e) => onUpdateButton(section.id, bIdx, "label", e.target.value)}
                      placeholder="Button label" />
                  </Col>
                  <Col xs={6}>
                    <Form.Select size="sm" value={btn.action}
                      onChange={(e) => onUpdateButton(section.id, bIdx, "action", e.target.value)}>
                      <option value="booking">🏨 Booking URL</option>
                      <option value="whatsapp">💬 WhatsApp</option>
                      <option value="call">📞 Call</option>
                      <option value="url">🔗 Custom URL</option>
                    </Form.Select>
                  </Col>
                  <Col xs={12}>
                    <Form.Control size="sm" value={btn.url}
                      onChange={(e) => onUpdateButton(section.id, bIdx, "url", e.target.value)}
                      placeholder={btn.action === "whatsapp" ? "https://wa.me/91..." : btn.action === "call" ? "tel:+91..." : "https://..."} />
                  </Col>
                </Row>
                {/* Button appearance */}
                <Row className="g-1 align-items-center">
                  <Col xs={3}>
                    <Form.Select size="sm" value={btn.style || "fill"}
                      onChange={(e) => onUpdateButton(section.id, bIdx, "style", e.target.value)}>
                      <option value="fill">Fill</option>
                      <option value="outline">Outline</option>
                      <option value="ghost">Ghost</option>
                    </Form.Select>
                  </Col>
                  <Col xs={2}>
                    <div className="d-flex align-items-center gap-1">
                      <Form.Control type="color" value={btn.color || "#0d6efd"}
                        onChange={(e) => onUpdateButton(section.id, bIdx, "color", e.target.value)}
                        title="Button colour"
                        style={{ padding: "2px 4px", height: 30, width: 40 }} />
                    </div>
                  </Col>
                  <Col xs={2}>
                    <Form.Control type="color" value={btn.textColor || "#ffffff"}
                      onChange={(e) => onUpdateButton(section.id, bIdx, "textColor", e.target.value)}
                      title="Text colour"
                      style={{ padding: "2px 4px", height: 30, width: 40 }} />
                  </Col>
                  <Col xs={5}>
                    <Form.Check type="checkbox" id={`btn-rounded-${btn.id}`} label="Rounded (pill)"
                      checked={btn.rounded !== false}
                      onChange={(e) => onUpdateButton(section.id, bIdx, "rounded", e.target.checked)} />
                  </Col>
                </Row>
                {/* Live button preview */}
                <div className="mt-2 text-center">
                  <div style={{
                    display: "inline-block",
                    padding: "8px 20px",
                    borderRadius: btn.rounded !== false ? 999 : 8,
                    background: btn.style === "fill" ? (btn.color || "#0d6efd") : "transparent",
                    border: `2px solid ${btn.color || "#0d6efd"}`,
                    color: btn.style === "fill" ? (btn.textColor || "#fff") : (btn.color || "#0d6efd"),
                    fontSize: "0.8rem", fontWeight: 600,
                  }}>
                    {btn.label || "Button"}
                  </div>
                </div>
              </div>
            ))}

            {/* Add button */}
            {section.buttons?.length < 4 && (
              <Button variant="outline-secondary" size="sm" className="w-100 mb-2"
                onClick={() => upd("buttons", [...(section.buttons||[]), {
                  id: `b${Date.now()}`, label: "New Button", action: "url", url: "",
                  style: "fill", color: "#6c757d", textColor: "#ffffff", rounded: true,
                }])}>
                <FaPlus className="me-1" size={10} />Add Button
              </Button>
            )}

            {/* Section background */}
            <BgPanel bg={section.bg || {}} onChange={updB} label="Section background" />
          </div>
        )}

        {/* ── FEEDBACK FORM ───────────────────────────────────────────────── */}
        {section.type === "feedback_form" && (
          <div>
            <div className="alert alert-info py-2 mb-3 d-flex gap-2" style={{ fontSize: "0.78rem" }}>
              <i className="fas fa-info-circle text-info mt-1 flex-shrink-0"></i>
              <span>
                <strong>Flow:</strong> WhatsApp message → this landing page → customer taps button → feedback form opens → response tracked in Feedback Responses.
              </span>
            </div>
            <Row className="g-2 mb-2">
              <Col xs={12}>
                <Form.Label className="fw-semibold small">Note shown above button</Form.Label>
                <Form.Control size="sm" value={section.note || ""}
                  onChange={(e) => upd("note", e.target.value)}
                  placeholder="e.g. Help us improve — share your experience" />
              </Col>
              <Col xs={8}>
                <Form.Label className="fw-semibold small">Button label</Form.Label>
                <Form.Control size="sm" value={section.buttonLabel || "Share Your Feedback"}
                  onChange={(e) => upd("buttonLabel", e.target.value)} />
              </Col>
              <Col xs={4}>
                <Form.Label className="fw-semibold small">Button colour</Form.Label>
                <Form.Control type="color" value={section.buttonColor || "#198754"}
                  onChange={(e) => upd("buttonColor", e.target.value)}
                  style={{ padding: "2px 4px", height: 34, width: "100%" }} />
              </Col>
            </Row>
            <Form.Label className="fw-semibold small">Survey ID or URL</Form.Label>
            <Form.Control size="sm" value={section.surveyId || ""}
              onChange={(e) => upd("surveyId", e.target.value)}
              placeholder="Paste survey ID from Feedback → Surveys" />
            <Form.Text className="text-muted">
              The link is tracked with guest_id + campaign_id for ROI attribution.
            </Form.Text>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── helpers ──────────────────────────────────────────────────────────────────
const resolveBg = (bg) => {
  if (!bg || bg.type === "none") return "transparent";
  if (bg.type === "solid")    return bg.color || "#ffffff";
  if (bg.type === "gradient") return `linear-gradient(${bg.gradDir||"135deg"}, ${bg.gradStart||"#0d6efd"}, ${bg.gradEnd||"#0dcaf0"})`;
  return "transparent";
};

// ─── Page Preview ──────────────────────────────────────────────────────────────
const PagePreview = ({ page, mode }) => (
  <div
    className="mx-auto overflow-hidden"
    style={{
      width: mode === "mobile" ? 300 : "100%",
      border: "2px solid #dee2e6",
      borderRadius: mode === "mobile" ? 24 : 12,
      minHeight: 500,
      background: "#fff",
      transition: "width 0.3s ease",
      boxShadow: mode === "mobile" ? "0 12px 40px rgba(0,0,0,0.18)" : "none",
    }}
  >
    {mode === "mobile" && (
      <div style={{ height: 20, background: "#f8f9fa", borderBottom: "1px solid #dee2e6",
        display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 60, height: 6, background: "#dee2e6", borderRadius: 3 }} />
      </div>
    )}

    {page?.sections?.map((s, i) => {
      const bgStyle = resolveBg(s.bg);

      switch (s.type) {

        case "banner": {
          // heightPct of the preview container (~500px)
          const h = Math.round(((s.heightPct || 40) / 100) * (mode === "mobile" ? 400 : 500));
          const hasBg = s.bg?.type && s.bg.type !== "none";
          return (
            <div key={i} style={{ position: "relative", height: h }}>
              {/* Image layer */}
              {s.value
                ? <img src={s.value} alt="Banner"
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    onError={(e) => { e.target.style.display = "none"; }} />
                : <div style={{ width:"100%", height:"100%",
                    background: hasBg ? bgStyle : "linear-gradient(135deg,#0d6efd,#0dcaf0)",
                    display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <div className="text-center"><FaImage size={28} color="rgba(255,255,255,0.5)" />
                      <div style={{ color:"rgba(255,255,255,0.6)", fontSize:"0.7rem", marginTop:4 }}>
                        {s.heightPct || 40}% height
                      </div>
                    </div>
                  </div>}
              {/* Gradient/tint over image */}
              {s.value && hasBg && (
                <div style={{ position:"absolute", inset:0, background: bgStyle, opacity: 0.6 }} />
              )}
              {/* Dark scrim from overlay.opacity */}
              {(s.overlay?.opacity || 0) > 0 && (
                <div style={{ position:"absolute", inset:0,
                  background: `${s.overlay.bgColor||"#000"}${Math.round((s.overlay.opacity||0)*255).toString(16).padStart(2,"0")}` }} />
              )}
              {/* Text overlay */}
              {s.overlay?.text && (
                <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", padding:"0 14px",
                  justifyContent: s.overlay.align==="left"?"flex-start":s.overlay.align==="right"?"flex-end":"center" }}>
                  <span style={{
                    fontFamily: s.overlay.font, color: s.overlay.color||"#fff",
                    fontSize: `${(s.overlay.size||28)*0.65}px`,
                    fontWeight: s.overlay.bold ? 700 : 400,
                    fontStyle: s.overlay.italic ? "italic" : "normal",
                    textAlign: s.overlay.align, textShadow:"0 1px 4px rgba(0,0,0,0.6)", lineHeight:1.3,
                  }}>{s.overlay.text}</span>
                </div>
              )}
            </div>
          );
        }

        case "gallery":
          return (
            <div key={i} className="d-flex gap-1 p-2">
              {(s.images||[]).map((img,gi) =>
                img
                  ? <img key={gi} src={img} alt={`Gallery ${gi+1}`}
                      style={{ flex:1, height:80, objectFit:"cover", borderRadius:6 }}
                      onError={(e) => { e.target.style.display="none"; }} />
                  : <div key={gi} style={{ flex:1, height:80, background:"#f8f9fa", borderRadius:6,
                      border:"1px dashed #dee2e6", display:"flex", alignItems:"center", justifyContent:"center" }}>
                      <FaImage color="#adb5bd" size={14} />
                    </div>
              )}
            </div>
          );

        case "title":
          return s.value
            ? <div key={i} style={{ padding:"12px 14px 0", background: bgStyle }}>
                <h5 style={{
                  fontFamily: s.style?.font  || "inherit",
                  fontSize:   `${(s.style?.size||24)*0.75}px`,
                  fontWeight: s.style?.bold !== false ? 700 : 400,
                  fontStyle:  s.style?.italic ? "italic" : "normal",
                  color:      s.style?.color || "#212529",
                  textAlign:  s.style?.align || "left",
                  margin: 0, lineHeight: 1.3,
                }}>{s.value}</h5>
              </div>
            : null;

        case "slideshow": {
          const imgs = s.images || [];
          return (
            <div key={i} style={{ position:"relative", height: mode==="mobile" ? 155 : 195 }}>
              {imgs.some(Boolean)
                ? <img src={imgs.find(Boolean)} alt="Slide"
                    style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} />
                : <div style={{ width:"100%", height:"100%", background:"linear-gradient(135deg,#6610f2,#0dcaf0)",
                    display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <div className="text-center text-white opacity-75">
                      <i className="fas fa-film" style={{ fontSize:"1.4rem" }}></i>
                      <div style={{ fontSize:"0.7rem", marginTop:4 }}>{imgs.length} slides · {(s.interval||3000)/1000}s</div>
                    </div>
                  </div>}
              <div style={{ position:"absolute", bottom:8, left:"50%", transform:"translateX(-50%)", display:"flex", gap:4 }}>
                {imgs.map((_,di) => (
                  <div key={di} style={{ width:5, height:5, borderRadius:"50%",
                    background: di===0 ? "#fff" : "rgba(255,255,255,0.4)" }} />
                ))}
              </div>
            </div>
          );
        }

        case "description":
        case "custom_text":
          return s.value
            ? <div key={i} style={{ padding:"8px 14px 0", background: bgStyle }}>
                <p style={{
                  fontFamily: s.style?.font  || "inherit",
                  fontSize:   `${(s.style?.size||14)*0.9}px`,
                  fontWeight: s.style?.bold  ? 700 : 400,
                  fontStyle:  s.style?.italic ? "italic" : "normal",
                  color:      s.style?.color || "#6b7280",
                  textAlign:  s.style?.align || "left",
                  lineHeight: 1.55, whiteSpace:"pre-wrap", margin: 0,
                }}>{s.value}</p>
              </div>
            : null;

        case "price":
          return s.value
            ? <div key={i} style={{ padding:"8px 14px 0", background: bgStyle }}>
                <div style={{
                  fontFamily: s.style?.font  || "inherit",
                  fontSize:   `${(s.style?.size||18)*0.85}px`,
                  fontWeight: s.style?.bold !== false ? 700 : 400,
                  fontStyle:  s.style?.italic ? "italic" : "normal",
                  color:      s.style?.color || "#dc2626",
                  textAlign:  s.style?.align || "left",
                }}>{s.value}</div>
              </div>
            : null;

        case "divider":
          return <hr key={i} className="mx-3 my-2" />;

        case "cta": {
          const ctaBg = resolveBg(s.bg);
          return (
            <div key={i} style={{
              padding: `${s.padding||16}px 14px`,
              background: ctaBg,
            }}>
              {s.buttons?.map((btn, bi) =>
                btn.label ? (
                  <div key={bi} style={{ marginBottom: 8 }}>
                    <button style={{
                      width: "100%",
                      padding: "10px 16px",
                      borderRadius: btn.rounded !== false ? 999 : 8,
                      background: btn.style === "fill" ? (btn.color||"#0d6efd") : "transparent",
                      border: `2px solid ${btn.color||"#0d6efd"}`,
                      color: btn.style === "fill" ? (btn.textColor||"#fff") : (btn.color||"#0d6efd"),
                      fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
                    }}>
                      {btn.action==="whatsapp"?"💬 ":btn.action==="call"?"📞 ":"🏨 "}
                      {btn.label}
                    </button>
                  </div>
                ) : null
              )}
            </div>
          );
        }

        case "feedback_form":
          return (
            <div key={i} style={{ padding:"12px 14px 8px" }}>
              {s.note && <p style={{ fontSize:"0.75rem", color:"#6b7280", marginBottom:8 }}>{s.note}</p>}
              <button style={{
                width:"100%", padding:"11px 16px",
                background: s.buttonColor||"#198754",
                color:"#fff", border:"none", borderRadius:10,
                fontSize:"0.85rem", fontWeight:600, cursor:"pointer",
              }}>
                <i className="fas fa-poll me-2"></i>{s.buttonLabel||"Share Your Feedback"}
              </button>
              {!s.surveyId && (
                <p style={{ fontSize:"0.65rem", color:"#9ca3af", textAlign:"center", marginTop:5 }}>
                  No survey linked yet
                </p>
              )}
            </div>
          );

        default: return null;
      }
    })}

    <div className="text-center py-2 border-top mt-2" style={{ fontSize:"0.62rem", color:"#adb5bd" }}>
      Powered by Netshine Hospitality
    </div>
  </div>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const LandingPageManager = () => {
  const [pages,          setPages]          = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [editing,        setEditing]        = useState(null);
  const [showBuilder,    setShowBuilder]    = useState(false);
  const [previewMode,    setPreviewMode]    = useState("mobile");
  const [showPreview,    setShowPreview]    = useState(false);
  const [previewPage,    setPreviewPage]    = useState(null);
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState("");
  const [success,        setSuccess]        = useState("");
  const [showConfirm,    setShowConfirm]    = useState(false);
  const [confirmData,    setConfirmData]    = useState(null);
  const [showAddSection, setShowAddSection] = useState(false);

  useEffect(() => { fetchPages(); }, []);

  const fetchPages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/feedback/landing-pages`, authHeaders());
      const normalized = (res.data || []).map((p) => ({ ...p, id: p.pageid || p.id }));
      setPages(normalized);
    } catch {
      setPages([]);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing({ id: null, title: "", slug: `offer-${Date.now()}`, sections: DEFAULT_SECTIONS() });
    setShowBuilder(true);
    setError("");
  };

  const openEdit = (p) => {
    const sections = (p.sections?.length ? p.sections : DEFAULT_SECTIONS())
      .map((s, i) => ({ ...s, id: s.id || `s_${i}_${Date.now()}` }));
    setEditing({ ...p, sections });
    setShowBuilder(true);
    setError("");
  };

  // ── Section operations ─────────────────────────────────────────────────────
  const addSection    = (type) => { setEditing((p) => ({ ...p, sections: [...p.sections, newSection(type)] })); setShowAddSection(false); };
  const removeSection = (id)   => setEditing((p) => ({ ...p, sections: p.sections.filter((s) => s.id !== id) }));

  const updateSection = (id, field, value) =>
    setEditing((p) => ({ ...p, sections: p.sections.map((s) => s.id === id ? { ...s, [field]: value } : s) }));

  const updateButton = (sId, bIdx, field, value) =>
    setEditing((p) => ({
      ...p,
      sections: p.sections.map((s) => {
        if (s.id !== sId) return s;
        const buttons = s.buttons.map((b, i) => i === bIdx ? { ...b, [field]: value } : b);
        return { ...s, buttons };
      }),
    }));

  const updateGallery = (sId, imgIdx, url) =>
    setEditing((p) => ({
      ...p,
      sections: p.sections.map((s) => {
        if (s.id !== sId) return s;
        const images = [...(s.images || ["", "", ""])];
        images[imgIdx] = url;
        return { ...s, images };
      }),
    }));

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const arr = Array.from(editing.sections);
    const [moved] = arr.splice(result.source.index, 1);
    arr.splice(result.destination.index, 0, moved);
    setEditing((p) => ({ ...p, sections: arr }));
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const save = async () => {
    if (!editing.title.trim()) { setError("Page title is required."); return; }
    if (!editing.slug.trim())  { setError("Slug is required.");        return; }
    setSaving(true);
    setError("");
    try {
      if (editing.id) {
        await axios.put(`${apiUrl}/feedback/landing-pages/${editing.id}`, editing, authHeaders());
        setPages((p) => p.map((x) => x.id === editing.id ? { ...editing, views: x.views, clicks: x.clicks } : x));
      } else {
        const res = await axios.post(`${apiUrl}/feedback/landing-pages`, editing, authHeaders());
        setPages((p) => [res.data || { ...editing, id: `lp${Date.now()}`, status: "Active", views: 0, clicks: 0 }, ...p]);
      }
      setSuccess("Page saved successfully!");
      setShowBuilder(false);
    } catch {
      const saved = { ...editing, id: editing.id || `lp${Date.now()}`, status: "Active", views: 0, clicks: 0 };
      setPages((p) => editing.id ? p.map((x) => x.id === editing.id ? saved : x) : [saved, ...p]);
      setSuccess("Page saved (demo mode).");
      setShowBuilder(false);
    } finally {
      setSaving(false);
    }
  };

  const deletePage = async () => {
    try { await axios.delete(`${apiUrl}/feedback/landing-pages/${confirmData.id}`, authHeaders()); } catch { }
    setPages((p) => p.filter((x) => x.id !== confirmData.id));
    setShowConfirm(false);
  };

  if (loading) return <div className="d-flex justify-content-center py-5"><Spinner animation="border" variant="primary" /></div>;

  // ── LIST VIEW ──────────────────────────────────────────────────────────────
  if (!showBuilder) return (
    <div>
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h3 className="fw-bold text-dark mb-0">
              <i className="fas fa-file-image text-primary me-2"></i>Landing Pages
            </h3>
            <p className="text-muted mb-0 mt-1">Mobile-first offer pages — images, text, pricing, and CTA buttons</p>
          </div>
          <Button variant="primary" className="px-4" onClick={openCreate}>
            <FaPlus className="me-2" />Create Page
          </Button>
        </div>
      </div>

      {success && <Alert variant="success" dismissible onClose={() => setSuccess("")}>{success}</Alert>}

      <Row className="g-4">
        {pages.map((p) => (
          <Col md={6} lg={4} key={p.id}>
            <Card className="shadow-sm border-0 h-100 lp-card">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h6 className="fw-bold text-dark mb-1">{p.title}</h6>
                    <code className="text-muted small">/o/{p.slug}</code>
                  </div>
                  <Badge bg={p.status === "Active" ? "success" : "secondary"} pill>{p.status}</Badge>
                </div>
                <div className="d-flex gap-4">
                  <div>
                    <div className="fw-bold text-primary h5 mb-0">{p.views || 0}</div>
                    <small className="text-muted">Views</small>
                  </div>
                  <div>
                    <div className="fw-bold text-success h5 mb-0">{p.clicks || 0}</div>
                    <small className="text-muted">Clicks</small>
                  </div>
                  {p.views > 0 && (
                    <div>
                      <div className="fw-bold h5 mb-0">{Math.round((p.clicks / p.views) * 100)}%</div>
                      <small className="text-muted">CTR</small>
                    </div>
                  )}
                </div>
              </Card.Body>
              <Card.Footer className="bg-white border-top d-flex justify-content-between px-4 py-3">
                <OverlayTrigger placement="top" overlay={<Tooltip>Edit page</Tooltip>}>
                  <Button variant="light" size="sm" className="p-2" onClick={() => openEdit(p)}><FaEdit className="text-primary" /></Button>
                </OverlayTrigger>
                <OverlayTrigger placement="top" overlay={<Tooltip>Preview page</Tooltip>}>
                  <Button variant="light" size="sm" className="p-2"
                    onClick={() => {
                      const sections = (p.sections?.length ? p.sections : DEFAULT_SECTIONS())
                        .map((s, i) => ({ ...s, id: s.id || `s_${i}` }));
                      setPreviewPage({ ...p, sections });
                      setShowPreview(true);
                    }}>
                    <FaEye className="text-primary" />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger placement="top" overlay={<Tooltip>Copy link</Tooltip>}>
                  <Button variant="light" size="sm" className="p-2"
                    onClick={() => navigator.clipboard?.writeText?.(`/o/${p.slug}`)}>
                    <FaCopy className="text-info" />
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
                  <Button variant="light" size="sm" className="p-2"
                    onClick={() => { setConfirmData(p); setShowConfirm(true); }}>
                    <FaTrash className="text-danger" />
                  </Button>
                </OverlayTrigger>
              </Card.Footer>
            </Card>
          </Col>
        ))}

        {/* Add new */}
        <Col md={6} lg={4}>
          <Card className="shadow-sm border-0 h-100 text-center d-flex align-items-center justify-content-center lp-blank-card"
            style={{ minHeight: 160, cursor: "pointer" }}
            onClick={openCreate} aria-label="Create New Landing Page">
            <Card.Body>
              <h6 className="fw-bold text-primary mb-2">New Page</h6>
              <p className="text-muted small mb-3">Create a mobile-first offer page with images, text and CTA buttons</p>
              <FaPlus className="text-primary" style={{ fontSize: "1.5rem" }} />
            </Card.Body>
          </Card>
        </Col>

        {pages.length === 0 && (
          <Col xs={12} className="text-center py-5">
            <div style={{ fontSize: "3rem" }}>📄</div>
            <h5 className="mt-3 fw-bold">No landing pages yet</h5>
            <p className="text-muted">Build your first offer page and link it to a WhatsApp template.</p>
            <Button variant="primary" onClick={openCreate}><FaPlus className="me-2" />Create Page</Button>
          </Col>
        )}
      </Row>

      {/* ── Full-screen Page Preview Modal ──────────────────────────────── */}
      <Modal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        size="xl"
        dialogClassName="modal-right"
        style={{ height: "100vh", margin: 0 }}
        aria-label="Page Preview"
      >
        <Modal.Header style={{ padding: "14px 24px", borderBottom: "1px solid #e5e7eb", position: "sticky", top: 0, zIndex: 10, background: "#fff" }}>
          <div className="d-flex align-items-center gap-3 w-100">
            <Modal.Title style={{ fontSize: "1rem", fontWeight: 700 }}>
              <FaEye className="text-primary me-2" />
              {previewPage?.title}
              <code className="ms-2 text-muted" style={{ fontSize: "0.78rem" }}>/o/{previewPage?.slug}</code>
            </Modal.Title>
            {/* Mobile / Desktop toggle */}
            <div className="d-flex gap-1 ms-3">
              <Button size="sm"
                variant={previewMode === "mobile" ? "primary" : "outline-secondary"}
                onClick={() => setPreviewMode("mobile")}>
                <FaMobile className="me-1" />Mobile
              </Button>
              <Button size="sm"
                variant={previewMode === "desktop" ? "primary" : "outline-secondary"}
                onClick={() => setPreviewMode("desktop")}>
                <FaDesktop className="me-1" />Desktop
              </Button>
            </div>
            <div className="ms-auto d-flex gap-2 align-items-center">
              <Button size="sm" variant="outline-primary"
                onClick={() => { setShowPreview(false); openEdit(previewPage); }}>
                <FaEdit className="me-1" />Edit Page
              </Button>
              {/* Prominent close */}
              <button onClick={() => setShowPreview(false)}
                style={{ background: "#f3f4f6", border: "none", borderRadius: "50%", width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 18, color: "#374151" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#fee2e2"; e.currentTarget.style.color = "#dc2626"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#f3f4f6"; e.currentTarget.style.color = "#374151"; }}
                aria-label="Close preview"
              >×</button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body style={{ overflowY: "auto", background: "#f0f2f5", padding: "32px 24px" }}>
          <div style={{
            margin:    "0 auto",
            maxWidth:  previewMode === "mobile" ? 390 : "100%",
            transition:"max-width 0.3s ease",
          }}>
            {/* Page metadata bar */}
            <div className="d-flex justify-content-between align-items-center mb-3 px-1">
              <div>
                <Badge bg={previewPage?.status === "Active" ? "success" : "secondary"} className="me-2">
                  {previewPage?.status}
                </Badge>
                <small className="text-muted">
                  <i className="fas fa-eye me-1"></i>{previewPage?.views || 0} views ·
                  <i className="fas fa-mouse-pointer ms-2 me-1"></i>{previewPage?.clicks || 0} clicks
                </small>
              </div>
              <small className="text-muted">
                {previewPage?.sections?.length || 0} sections
              </small>
            </div>
            {/* Render the page using PagePreview component */}
            <PagePreview page={previewPage} mode={previewMode} />
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton><Modal.Title>Confirm Delete</Modal.Title></Modal.Header>
        <Modal.Body>Delete <strong>{confirmData?.title}</strong>? This cannot be undone.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>No</Button>
          <Button variant="danger" onClick={deletePage}>Yes, Delete</Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .lp-card { transition: all 0.25s ease; border-radius: 12px !important; }
        .lp-card:hover { transform: translateY(-3px); box-shadow: 0 6px 16px rgba(0,0,0,0.1) !important; border-left: 4px solid var(--bs-primary) !important; }
        .lp-card .card-footer { border-top: 1px solid #f0f0f0 !important; }
        .lp-blank-card { border: 2px dashed #0d6efd !important; border-radius: 12px !important; background: #f8faff !important; transition: all 0.3s ease; }
        .lp-blank-card:hover { transform: translateY(-4px); box-shadow: 0 6px 16px rgba(0,0,0,0.12) !important; background: #eef6ff !important; }
        .page-header { margin-bottom: 1.5rem; }
      `}</style>
    </div>
  );

  // ── BUILDER VIEW ───────────────────────────────────────────────────────────
  return (
    <div>
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <Button variant="secondary" onClick={() => setShowBuilder(false)}>
              <FaArrowLeft className="me-1" />Back
            </Button>
            <div>
              <h3 className="fw-bold text-dark mb-0">
                <i className="fas fa-tools text-primary me-2"></i>
                {editing?.id ? "Edit Page" : "Build Landing Page"}
              </h3>
              <small className="text-muted">
                Upload images · Add text · Drag sections to reorder · Preview live
              </small>
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary"
              onClick={() => setPreviewMode((m) => m === "mobile" ? "desktop" : "mobile")}>
              {previewMode === "mobile"
                ? <><FaDesktop className="me-1" />Desktop</>
                : <><FaMobile className="me-1" />Mobile</>}
            </Button>
            <Button variant="primary" onClick={save} disabled={saving} className="px-4"
              aria-label="Save page">
              {saving
                ? <><Spinner size="sm" animation="border" className="me-1" />Saving...</>
                : <><FaSave className="me-1" />Save Page</>}
            </Button>
          </div>
        </div>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

      <Row>
        {/* Builder */}
        <Col md={6}>
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="fw-bold mb-0">
                <i className="fas fa-sliders-h text-primary me-2"></i>Page Settings
              </h6>
            </Card.Header>
            <Card.Body className="p-4">
              <Row className="g-2 mb-4">
                <Col xs={7}>
                  <Form.Label className="fw-semibold">Title <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    value={editing?.title || ""}
                    onChange={(e) => setEditing((p) => ({ ...p, title: e.target.value }))}
                    placeholder="Weekend Spa Package"
                    aria-required="true"
                  />
                </Col>
                <Col xs={5}>
                  <Form.Label className="fw-semibold">
                    Slug <span className="text-muted fw-normal">/o/</span>
                  </Form.Label>
                  <Form.Control
                    value={editing?.slug || ""}
                    onChange={(e) =>
                      setEditing((p) => ({ ...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") }))
                    }
                    placeholder="weekend-spa"
                    aria-label="URL slug"
                  />
                </Col>
              </Row>

              <div className="d-flex justify-content-between align-items-center mb-1">
                <Form.Label className="fw-semibold mb-0">
                  Content Sections
                  <Badge bg="secondary" className="ms-2">{editing?.sections?.length || 0}</Badge>
                </Form.Label>
                <Button variant="outline-primary" size="sm" onClick={() => setShowAddSection(true)}>
                  <FaPlus className="me-1" />Add Section
                </Button>
              </div>
              <small className="text-muted d-block mb-3">
                <FaGripVertical size={10} className="me-1" />Drag grip handle to reorder · Click trash to remove
              </small>

              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="lp-sections">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {editing?.sections?.map((section, idx) => (
                        <Draggable key={section.id} draggableId={section.id} index={idx}>
                          {(prov, snap) => (
                            <div
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              style={{ ...prov.draggableProps.style, opacity: snap.isDragging ? 0.8 : 1 }}
                            >
                              <SectionRow
                                section={section}
                                dragHandleProps={prov.dragHandleProps}
                                onUpdate={updateSection}
                                onRemove={removeSection}
                                onUpdateButton={updateButton}
                                onUpdateGallery={updateGallery}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              <div className="text-center mt-3">
                <Button variant="light" size="sm" className="border px-4"
                  onClick={() => setShowAddSection(true)}>
                  <FaPlus className="me-1" />Add Section
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Preview */}
        <Col md={6}>
          <Card className="shadow-sm border-0" style={{ position: "sticky", top: 80 }}>
            <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
              <h6 className="fw-bold mb-0">
                <FaEye className="text-primary me-2" />
                {previewMode === "mobile" ? "📱 Mobile Preview" : "🖥️ Desktop Preview"}
              </h6>
              <small className="text-muted">
                <code>/o/{editing?.slug || "slug"}</code>
              </small>
            </Card.Header>
            <Card.Body className="p-3" style={{ overflowY: "auto", maxHeight: 620 }}>
              <PagePreview page={editing} mode={previewMode} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add Section Modal */}
      <Modal show={showAddSection} onHide={() => setShowAddSection(false)} centered aria-label="Add Section">
        <Modal.Header closeButton>
          <Modal.Title>Add Section</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pb-3">
          {SECTION_TYPES.map((sec) => (
            <Button
              key={sec.type}
              variant="light"
              className="w-100 d-flex align-items-center gap-3 text-start mb-2 p-3 border"
              style={{ borderRadius: 8 }}
              onClick={() => addSection(sec.type)}
              aria-label={`Add ${sec.label}`}
            >
              <i className={`fas ${sec.icon}`} style={{ color: sec.color, width: 22, textAlign: "center", fontSize: "1rem" }}></i>
              <div>
                <div className="fw-semibold" style={{ fontSize: "0.88rem" }}>{sec.label}</div>
                <small className="text-muted">{sec.desc}</small>
              </div>
            </Button>
          ))}
        </Modal.Body>
      </Modal>

      <style>{`
        .card { border-radius: 12px !important; }
        .card-header { border-radius: 12px 12px 0 0 !important; }
        .page-header { margin-bottom: 1.5rem; }
      `}</style>
    </div>
  );
};

export default LandingPageManager;
